import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtMoney, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary } from "../components/Modal";
import { toast } from "sonner";
import { Plus, ArrowLeftRight, ClipboardCheck } from "lucide-react";

export default function Cash() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [accounts, setAccounts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [closings, setClosings] = useState([]);
  const [accOpen, setAccOpen] = useState(false);
  const [trOpen, setTrOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [accName, setAccName] = useState("");
  const [tr, setTr] = useState({ from_account_id: "", to_account_id: "", amount: "" });
  const [closing, setClosing] = useState({ account_id: "", counted: "" });

  const load = () => {
    api.get("/cash/accounts").then((r) => setAccounts(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/cash/movements").then((r) => setMovements(r.data)).catch(() => {});
    api.get("/cash/closings").then((r) => setClosings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addAccount = async () => {
    try {
      await api.post("/cash/accounts", { name: accName, type: "other" });
      toast.success("Compte créé");
      setAccOpen(false); setAccName("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doTransfer = async () => {
    try {
      await api.post("/cash/transfer", { ...tr, amount: Number(tr.amount) });
      toast.success("Transfert effectué");
      setTrOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doClosing = async () => {
    try {
      const r = await api.post("/cash/closing", { ...closing, counted: Number(closing.counted) });
      const v = r.data.variance;
      if (Math.abs(v) < 0.01) toast.success("Clôture conforme — aucun écart");
      else toast.warning(`Écart de caisse : ${fmtMoney(v, cur)}`);
      setCloseOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const movCols = [
    { key: "created_at", label: "Date", render: (m) => fmtDateTime(m.created_at) },
    { key: "account_name", label: "Compte" },
    { key: "label", label: "Libellé" },
    { key: "source", label: "Source" },
    { key: "amount", label: "Montant", className: "text-right", render: (m) => (
      <span className={`font-money font-bold ${m.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>{m.direction === "in" ? "+" : "-"}{fmtMoney(m.amount, cur)}</span>) },
    { key: "balance_after", label: "Solde après", className: "text-right", render: (m) => <span className="font-money">{fmtMoney(m.balance_after, cur)}</span> },
    { key: "user_name", label: "Par" },
  ];

  const closeCols = [
    { key: "created_at", label: "Date", render: (c) => fmtDateTime(c.created_at) },
    { key: "account_name", label: "Compte" },
    { key: "theoretical", label: "Théorique", className: "text-right", render: (c) => <span className="font-money">{fmtMoney(c.theoretical, cur)}</span> },
    { key: "counted", label: "Compté", className: "text-right", render: (c) => <span className="font-money">{fmtMoney(c.counted, cur)}</span> },
    { key: "variance", label: "Écart", className: "text-right", render: (c) => (
      <span className={`font-money font-bold ${Math.abs(c.variance) < 0.01 ? "text-emerald-600" : "text-rose-600"}`}>{fmtMoney(c.variance, cur)}</span>) },
    { key: "created_by", label: "Par" },
  ];

  return (
    <div className="space-y-4" data-testid="cash-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Caisse & Trésorerie</h1>
        {hasPerm("cash.manage") && (
          <div className="flex gap-2 flex-wrap">
            <button data-testid="add-account-btn" onClick={() => setAccOpen(true)} className={btnSecondary}><Plus className="h-4 w-4" /> Compte</button>
            <button data-testid="cash-transfer-btn" onClick={() => setTrOpen(true)} className={btnSecondary}><ArrowLeftRight className="h-4 w-4" /> Transfert</button>
            <button data-testid="cash-closing-btn" onClick={() => setCloseOpen(true)} className={btnPrimary}><ClipboardCheck className="h-4 w-4" /> Clôture de caisse</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cash-accounts">
        {accounts.map((a) => (
          <div key={a.id} data-testid={`cash-account-${a.id}`} className="bg-card border rounded-xl p-4">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{a.name}</div>
            <div className={`font-money font-bold text-xl mt-1 ${a.balance < 0 ? "text-rose-600" : ""}`}>{fmtMoney(a.balance, cur)}</div>
          </div>
        ))}
      </div>

      <h2 className="font-heading font-bold text-lg">Mouvements de trésorerie</h2>
      <DataTable testid="cash-movements" columns={movCols} rows={movements} searchKeys={["label", "account_name", "user_name", "ref"]} pageSize={15} />

      <h2 className="font-heading font-bold text-lg">Clôtures quotidiennes</h2>
      <DataTable testid="cash-closings" columns={closeCols} rows={closings} searchKeys={["account_name", "created_by"]} pageSize={10} />

      <Modal open={accOpen} onClose={() => setAccOpen(false)} title="Nouveau compte financier">
        <div className="space-y-3">
          <Field label="Nom"><input data-testid="acc-name" value={accName} onChange={(e) => setAccName(e.target.value)} className={inputCls} placeholder="Ex: Banque BICICI, Moov Money" /></Field>
          <button data-testid="acc-submit" onClick={addAccount} disabled={!accName} className={`${btnPrimary} w-full`}>Créer</button>
        </div>
      </Modal>

      <Modal open={trOpen} onClose={() => setTrOpen(false)} title="Transfert entre comptes">
        <div className="space-y-3">
          <Field label="De">
            <select data-testid="cash-tr-from" value={tr.from_account_id} onChange={(e) => setTr({ ...tr, from_account_id: e.target.value })} className={selectCls}>
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({fmtMoney(a.balance, cur)})</option>)}
            </select>
          </Field>
          <Field label="Vers">
            <select data-testid="cash-tr-to" value={tr.to_account_id} onChange={(e) => setTr({ ...tr, to_account_id: e.target.value })} className={selectCls}>
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Montant"><input data-testid="cash-tr-amount" type="number" min="0" value={tr.amount} onChange={(e) => setTr({ ...tr, amount: e.target.value })} className={inputCls} /></Field>
          <button data-testid="cash-tr-submit" onClick={doTransfer} disabled={!tr.from_account_id || !tr.to_account_id || !Number(tr.amount)} className={`${btnPrimary} w-full`}>Transférer</button>
        </div>
      </Modal>

      <Modal open={closeOpen} onClose={() => setCloseOpen(false)} title="Clôture de caisse quotidienne">
        <div className="space-y-3">
          <Field label="Compte">
            <select data-testid="closing-account" value={closing.account_id} onChange={(e) => setClosing({ ...closing, account_id: e.target.value })} className={selectCls}>
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} — théorique : {fmtMoney(a.balance, cur)}</option>)}
            </select>
          </Field>
          <Field label="Montant réellement compté"><input data-testid="closing-counted" type="number" min="0" value={closing.counted} onChange={(e) => setClosing({ ...closing, counted: e.target.value })} className={inputCls} /></Field>
          <button data-testid="closing-submit" onClick={doClosing} disabled={!closing.account_id || closing.counted === ""} className={`${btnPrimary} w-full`}>Valider la clôture</button>
        </div>
      </Modal>
    </div>
  );
}
